import type { StatusLabel } from "../../common/constants/order-display.constants";
import { LCR_DELIVERY_METHOD, type LcrDeliveryMethod } from "./lcr-order.types";

export interface CreateOrderRequest {
  items: Record<
    string,
    {
      quantity: number;
      details?: {
        returnedDeviceSerialNumber?: string;
      };
    }
  >;
  deliveryTo: {
    fullName: string;
    country: string;
    countryISO3: string;
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    city: string;
    citySubdivision1: string;
    county: string;
    postalCode: string;
    countryCode: string;
    phoneNumber: string;
    deliveryInstructions?: string;
  };
  details: {
    checkoutInfo: {
      costCenter: string;
      carCode: string;
      businessJustification: string;
    };
    deliverTo: {
      country: string;
    };
  };
  acknowledgementAudit: {
    by: string;
    on: Date;
    policyName: string;
    acknowledged: boolean;
  }[];
  labels: string[];
}

export interface CreateOrderResponse {
  orderId: string;
  orderReference: string;
  canBeAutoApproved: boolean;
}

export interface HopOrder {
  status?: string;
  identifier?: string;
  assetTag?: string;
  serialNumber?: string;
  shippingCarrier?: string;
  trackingNumber?: string;
  securityTapeSerialNumber?: string;
}

export interface OrderItem {
  itemId: string;
  status?: string;
  price?: { amount?: number; currency?: string };
  itemInformation?: {
    name?: string;
  };
  details?: {
    hopOrders?: HopOrder[];
    poNumber?: string;
    returnedDeviceSerialNumber?: string;
  } | null;
}

export const ORDER_SOURCE_IDS = { ito: "ito", lcr: "lcr" } as const;
export type OrderSourceId = (typeof ORDER_SOURCE_IDS)[keyof typeof ORDER_SOURCE_IDS];

/** The amznham consumer's statuses in ITOBackend `configuration/consumers/amznham/status.json`. */
export const ITO_ORDER_STATUS = {
  new: "NEW",
  processing: "PROCESSING",
  pendingApproval: "PENDING_APPROVAL",
  approved: "APPROVED",
  pendingShipping: "PENDING_SHIPPING",
  shipped: "SHIPPED",
  delivered: "DELIVERED",
  rejected: "REJECTED",
  cancelled: "CANCELLED",
} as const;

export type ItoOrderStatus = (typeof ITO_ORDER_STATUS)[keyof typeof ITO_ORDER_STATUS];

export { LCR_DELIVERY_METHOD as DELIVERY_METHODS };
export type DeliveryMethod = LcrDeliveryMethod;

export interface Order {
  orderId: string;
  orderReference?: string;
  source?: OrderSourceId;
  deliveryMethod?: DeliveryMethod;
  status?: string;
  /** The status label, set by the order source that loaded the order. */
  statusLabel?: StatusLabel;
  /** An LCR order's replaced laptop serial number, which keys its tracking page. */
  replacedSerialNumber?: string;
  requested?: { by?: string; on?: string };
  items?: OrderItem[];
  total?: {
    amount?: number;
    currency?: string;
  };
  details?: {
    checkoutInfo?: {
      costCenter?: string;
      carCode?: string;
      businessJustification?: string;
    };
  };
  requester?: {
    basicInfo?: {
      firstName?: string;
      lastName?: string;
    };
  };
  deliveryTo?: {
    fullName?: string;
    addressLineData?: string[];
    city?: string;
    citySubdivision1?: string;
    regionSubdivision1?: string;
    county?: string;
    country?: string;
    postalCode?: string;
    deliveryInstructions?: string;
    phoneNumber?: string;
  };
  orderSupport?: OrderSupport;
}

export interface OrderSupport {
  supportRequested: boolean;
  lastCommentInfo?: {
    on: string;
    by: string;
  };
}
